import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api.js';
import { PageLayout } from '@/components/layout/PageLayout.js';
import { NAV_LABELS } from '@/lib/nav-labels.js';

type Trilha = {
  id: string;
  slug: string;
  titulo: string;
  descricao?: string | null;
  tipo?: string;
  programas: { id: string; slug: string; titulo: string }[];
};

type Props = {
  tipo: 'JORNADA' | 'MICRO';
  title: string;
  description: string;
};

export function ConhecimentoTrilhasPage({ tipo, title, description }: Props) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['trilhas', tipo],
    queryFn: async () => {
      const r = await api.get<Trilha[]>(`/conteudo/trilhas?tipo=${tipo}`);
      return r.data;
    },
  });

  if (isLoading) {
    return <PageLayout title={title}>Carregando…</PageLayout>;
  }
  if (error) {
    return (
      <PageLayout title={title}>
        <p className="text-feedback">Não foi possível carregar o conteúdo.</p>
      </PageLayout>
    );
  }

  return (
    <PageLayout title={title} description={description}>
      {!data?.length ? (
        <p className="text-ha-subtle">Nenhuma trilha publicada ainda.</p>
      ) : (
        <ul className="grid gap-4 md:grid-cols-2">
          {data.map((t) => (
            <li
              key={t.id}
              className="rounded-card border border-ha-border2 bg-ha-card p-5 transition hover:border-ha-border"
            >
              <Link
                to={`/aprendizado/trilha/${t.slug}`}
                className="font-semibold text-accent hover:underline"
              >
                {t.titulo}
              </Link>
              {t.descricao && <p className="mt-2 text-sm text-ha-subtle">{t.descricao}</p>}
              {t.programas.length > 0 && (
                <ul className="mt-3 flex flex-col gap-1 border-t border-ha-border pt-3 text-sm text-ha-muted">
                  {t.programas.map((p) => (
                    <li key={p.id}>{p.titulo}</li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </PageLayout>
  );
}

export function JornadasPage() {
  return (
    <ConhecimentoTrilhasPage
      tipo="JORNADA"
      title={NAV_LABELS.jornadas}
      description="Trilhas estruturadas com progressão definida."
    />
  );
}

export function MicroLearningPage() {
  return (
    <ConhecimentoTrilhasPage
      tipo="MICRO"
      title={NAV_LABELS.microLearning}
      description="Conteúdos curtos para consumo rápido (5–15 min por aula)."
    />
  );
}
